"use client";

import {
  BaselineIcon,
  CalendarIcon,
  CheckSquareIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  FileIcon,
  HashIcon,
  LinkIcon,
  ListChecksIcon,
  ListIcon,
  TextCursorInputIcon,
} from "lucide-react";
import * as React from "react";

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils/index";

function getColumnVariant(variant) {
  switch (variant) {
    case "short-text":
      return { icon: BaselineIcon, label: "Short text" };
    case "long-text":
      return { icon: TextCursorInputIcon, label: "Long text" };
    case "number":
      return { icon: HashIcon, label: "Number" };
    case "url":
      return { icon: LinkIcon, label: "URL" };
    case "checkbox":
      return { icon: CheckSquareIcon, label: "Checkbox" };
    case "select":
      return { icon: ListIcon, label: "Select" };
    case "multi-select":
      return { icon: ListChecksIcon, label: "Multi-select" };
    case "date":
      return { icon: CalendarIcon, label: "Date" };
    case "file":
      return { icon: FileIcon, label: "File" };
    default:
      return null;
  }
}

/**
 * DataGridColumnHeader
 *
 * Simpler (and more familiar) sorting UX:
 * - Click header: toggles sort (asc → desc → none)
 * - Single-column sort only (no multi-sort)
 *
 * We intentionally removed the "Sort" menu & multi-sort UI.
 */
export function DataGridColumnHeader({ header, table, className, ...props }) {
  const column = header.column;

  const label = column.columnDef.meta?.label
    ? column.columnDef.meta.label
    : typeof column.columnDef.header === "string"
      ? column.columnDef.header
      : column.id;

  const isAnyColumnResizing =
    table.getState().columnSizingInfo.isResizingColumn;

  const cellVariant = column.columnDef.meta?.cell;
  const columnVariant = getColumnVariant(cellVariant?.variant);

  const sortState = column.getIsSorted(); // 'asc' | 'desc' | false
  const canSort = column.getCanSort();

  const SortIcon =
    sortState === "asc" ? ChevronUpIcon : sortState === "desc" ? ChevronDownIcon : null;

  const onToggleSort = React.useCallback(
    (event) => {
      // Keep resizing interactions clean
      if (isAnyColumnResizing) return;

      // Left click only
      if (event?.button !== 0) return;

      if (!canSort) return;

      // Enforce single-column sort, and cycle: none -> asc -> desc -> none
      table.setSorting((prev) => {
        const current = prev?.[0];
        const isThis = current?.id === column.id;

        if (!isThis) {
          return [{ id: column.id, desc: false }];
        }

        if (current?.desc === false) {
          return [{ id: column.id, desc: true }];
        }

        // current desc=true -> clear
        return [];
      });
    },
    [table, column.id, canSort, isAnyColumnResizing],
  );

  return (
    <div className="relative size-full">
      <button
        type="button"
        className={cn(
          "flex size-full items-center justify-between gap-2 p-2 text-sm",
          "hover:bg-accent/40",
          "text-shadow-sm",
          canSort && "cursor-pointer",
          isAnyColumnResizing && "pointer-events-none",
          className,
        )}
        onPointerDown={onToggleSort}
        {...props}>
        <div className="flex min-w-0 flex-1 items-center gap-1.5">
          {columnVariant && (
            <Tooltip delayDuration={100}>
              <TooltipTrigger asChild>
                <columnVariant.icon className="size-3.5 shrink-0 text-muted-foreground" />
              </TooltipTrigger>
              <TooltipContent side="top">
                <p>{columnVariant.label}</p>
              </TooltipContent>
            </Tooltip>
          )}
          <span className="truncate">{label}</span>
        </div>

        <div className="flex items-center gap-1 text-muted-foreground">
          {SortIcon ? <SortIcon className="size-4" /> : <span className="size-4" />}
        </div>
      </button>

      {header.column.getCanResize() && (
        <DataGridColumnResizer header={header} table={table} label={label} />
      )}
    </div>
  );
}

const DataGridColumnResizer = React.memo(
  DataGridColumnResizerImpl,
  (prev, next) => {
    const prevColumn = prev.header.column;
    const nextColumn = next.header.column;

    if (
      prevColumn.getIsResizing() !== nextColumn.getIsResizing() ||
      prevColumn.getSize() !== nextColumn.getSize()
    ) {
      return false;
    }

    if (prev.label !== next.label) return false;

    return true;
  },
);

function DataGridColumnResizerImpl({ header, table, label }) {
  const defaultColumnDef = table._getDefaultColumnDef();

  const onDoubleClick = React.useCallback(() => {
    header.column.resetSize();
  }, [header.column]);

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={`Resize ${label} column`}
      aria-valuenow={header.column.getSize()}
      aria-valuemin={defaultColumnDef.minSize}
      aria-valuemax={defaultColumnDef.maxSize}
      tabIndex={0}
      className={cn(
        "after:-translate-x-1/2 -end-px absolute top-0 z-50 h-full w-0.5 cursor-ew-resize touch-none select-none bg-border transition-opacity",
        "after:absolute after:inset-y-0 after:start-1/2 after:h-full after:w-[18px] after:content-['']",
        "hover:bg-primary focus:bg-primary focus:outline-none",
        header.column.getIsResizing() ? "bg-primary" : "opacity-0 hover:opacity-100",
      )}
      onDoubleClick={onDoubleClick}
      onMouseDown={header.getResizeHandler()}
      onTouchStart={header.getResizeHandler()} />
  );
}
